import { createFileRoute, Link } from '@tanstack/react-router';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { Logo } from '~/generated/svg/logo/Logo';
import { ValiantLogo } from '~/generated/svg/logo/ValiantLogo';
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  HomeIcon,
  LockClosedIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { formatPrice } from '~/helper/formatPrice';
import { formatDateTime } from '~/helper/formatDateTime';
import { useTranslate } from '@tolgee/react';
import { useEffect, useRef } from 'react';
import { trackEvent } from '@agentselly/analytics';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { V3ErrorBlock, V3LoadingBlock } from '~/components/public/V3States';

export interface ValiantDossierItem {
  id: string;
  sessionId?: string;
  dossierId?: string;
  propertyAddress?: string;
  property?: {
    numberOfRooms?: number;
    propertyType?: string;
  };
  evaluation?: {
    timestamp?: string;
    price?: number;
  };
}

export interface ResolveValiantTargetLinkInput {
  dossierId?: string;
  sessionId?: string;
}

export interface ResolveValiantTargetLinkOutput {
  to: AppRoute;
  params: Record<string, string>;
  search: {
    valiantDeviceId: string;
  };
  kind: 'session';
}

export function resolveValiantTargetLink(
  dossier: ResolveValiantTargetLinkInput,
  params?: Record<string, string>,
): ResolveValiantTargetLinkOutput {
  const baseParams = params || {};
  const valiantDeviceId = baseParams.valiantDeviceId ?? '';

  return {
    to: AppRoute.$LANG_$COMPANYID_PUBLIC_EVALUATION_RESULT_$SESSIONID,
    params: {
      ...baseParams,
      sessionId: dossier.sessionId || '',
    },
    search: {
      valiantDeviceId,
    },
    kind: 'session',
  };
}

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PUBLIC_VALIANT_$VALIANTDEVICEID,
)({
  component: Body,
  staleTime: 0,
});

function Body() {
  const { t } = useTranslate();

  const params = useAppParams(Route);
  const v3 = useV3Redesign();

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent('immodossier valiant overview shown');
  }, []);

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_PUBLIC_VALIANT_$VALIANTDEVICEID,
    {
      params,
    },
  );

  if (query.isLoading && v3) {
    return <V3LoadingBlock />;
  }

  if (query.isLoading) {
    return (
      <div
        role="status"
        className="min-h-screen bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 flex items-center justify-center w-full h-full"
      >
        <svg
          aria-hidden="true"
          className="inline w-8 h-8 text-neutral-tertiary animate-spin text-gray-100 fill-primary-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  if ((query.isError || !query.data || query.data.error) && v3) {
    return (
      <div className="v3p-page">
        <V3ErrorBlock
          title={t(
            'v3_valiant_error_title',
            'Ihre Immobilien konnten nicht geladen werden',
          )}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <ErrorWidget error={query.error} />
      </div>
    );
  }

  if (query.data.error) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <ErrorWidget error={{ message: query.data.error }} />
      </div>
    );
  }

  const newDossierUrl = query.data.newDossierUrl;
  const dossiers = query.data.dossiers || [];

  if (v3) {
    const newDossierButton = newDossierUrl ? (
      <a
        className="v3-btn vbtn"
        href={newDossierUrl}
        target={newDossierUrl.startsWith('/') ? undefined : '_blank'}
        rel={newDossierUrl.startsWith('/') ? undefined : 'noreferrer'}
        data-track="immodossier valiant overview add dossier"
      >
        <PlusIcon
          className="w-4 h-4"
          aria-hidden="true"
        />
        {t('overview_button_add_dossier')}
      </a>
    ) : null;
    return (
      <div className="v3p-page">
        <div
          className="v3p-vbar"
          data-od-id="valiant-bar"
        >
          <span
            className="v3p-vlg"
            aria-hidden="true"
          >
            V
          </span>
          <b>Valiant</b>
          <span className="v3p-mu v3p-sm">
            {t('v3_valiant_bar_text', 'in Zusammenarbeit mit AgentSelly')}
          </span>
        </div>
        <div
          className="v3-head"
          data-od-id="page-head"
        >
          <h1>{t('v3_valiant_title', 'Ihre Immobilien')}</h1>
          <p>
            {t(
              'v3_valiant_subtitle',
              'Alle Bewertungen, die Sie über Valiant angefordert haben.',
            )}
          </p>
        </div>
        <section
          className="v3-card"
          data-od-id="dossier-list"
          aria-label={t('v3_valiant_title', 'Ihre Immobilien')}
        >
          {dossiers.length ? (
            <ul className="v3p-vl">
              {(dossiers as ValiantDossierItem[]).map((dossier) => {
                const isHouse = dossier.property?.propertyType === 'house';
                const target = resolveValiantTargetLink(
                  dossier,
                  params as Record<string, string>,
                );
                return (
                  <li key={dossier.id}>
                    <Link
                      className="vcard"
                      to={target.to}
                      params={target.params as unknown as never}
                      search={target.search as unknown as never}
                      data-track="immodossier valiant overview property card"
                    >
                      <span
                        className="v3p-ptype"
                        aria-hidden="true"
                      >
                        {isHouse ? (
                          <HomeIcon className="w-6 h-6" />
                        ) : (
                          <BuildingOfficeIcon className="w-6 h-6" />
                        )}
                      </span>
                      <span className="flex flex-col min-w-0">
                        <b>
                          {dossier.property?.numberOfRooms}-Zimmer{' '}
                          {isHouse ? 'Haus' : 'Wohnung'}
                        </b>
                        <span className="v3p-mu v3p-sm">
                          {dossier.propertyAddress}
                        </span>
                      </span>
                      <span className="r">
                        <b className="v3p-num">
                          {formatPrice(dossier.evaluation?.price || 0)}
                        </b>
                        <span className="v3p-mu v3p-xs">
                          {formatDateTime(dossier.evaluation?.timestamp || '')}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="v3p-center v3p-page">
              <p className="v3p-mu">{t('overview_no_dossiers')}</p>
              {newDossierButton}
            </div>
          )}
          {dossiers.length ? (
            <div className="v3p-row">{newDossierButton}</div>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 flex flex-col">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-lg font-semibold tracking-tight">
              <ValiantLogo className="h-5 w-auto" />
            </div>
            <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
            <div className="flex items-center gap-2 text-sm">
              <span>in Zusammenarbeit mit</span>
              <span className="font-semibold">
                <Logo className="h-4 w-auto" />
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-green-700">
            <LockClosedIcon className="w-3.5 h-3.5" />
            <span>Sichere Verbindung</span>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-16 pb-12 flex-1 w-full">
        <div>
          <h1 className="text-5xl sm:text-4xl tracking-tight leading-[1.05] mb-6 font-bold">
            Ihre Immobilien
          </h1>
        </div>

        <ul className="flex flex-col gap-4">
          {dossiers.length ? (
            (dossiers as ValiantDossierItem[]).map((dossier) => {
              const isHouse = dossier.property?.propertyType === 'house';
              const iconClass = 'w-6 h-6';
              const target = resolveValiantTargetLink(
                dossier,
                params as Record<string, string>,
              );

              return (
                <li key={dossier.id}>
                  <Link
                    to={target.to}
                    params={target.params as unknown as never}
                    search={target.search as unknown as never}
                    data-track="immodossier valiant overview property card"
                  >
                    <span className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm dark:shadow-gray-900/50 p-4 hover:bg-primary-50 dark:hover:bg-gray-700 cursor-pointer flex flex-col gap-4">
                      <span className="flex flex-row gap-2 items-center">
                        {isHouse ? (
                          <HomeIcon className={iconClass} />
                        ) : (
                          <BuildingOfficeIcon className={iconClass} />
                        )}
                        <span className="text-lg font-semibold">
                          {dossier.property?.numberOfRooms}-Zimmer{' '}
                          {isHouse ? 'Haus' : 'Wohnung'}
                        </span>
                        <span className="flex-1"></span>
                        <ArrowRightIcon className={iconClass} />
                      </span>

                      <span>{dossier.propertyAddress}</span>

                      <span className="flex flex-row gap-2 flex-wrap">
                        <span>
                          {formatDateTime(dossier.evaluation?.timestamp || '')}
                        </span>

                        <span className="flex-1"></span>

                        <span>
                          {formatPrice(dossier.evaluation?.price || 0)}
                        </span>
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })
          ) : (
            <div>
              <p>{t('overview_no_dossiers')}</p>
              <br />
              <div>
                {newDossierUrl ? (
                  <a
                    href={newDossierUrl}
                    className="inline-block rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-gray-700 p-4 font-bold"
                    target={
                      newDossierUrl.startsWith('/') ? undefined : '_blank'
                    }
                    rel={
                      newDossierUrl.startsWith('/') ? undefined : 'noreferrer'
                    }
                    data-track="immodossier valiant overview add dossier"
                  >
                    {t('overview_button_add_dossier')}
                  </a>
                ) : null}
              </div>
            </div>
          )}
        </ul>
      </section>

      <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs">
          <div>
            © {new Date().getFullYear()} Valiant Bank AG · AgentSelly AG
          </div>
          <div className="flex gap-4">
            <a
              href="https://www.agentselly.ch/agb/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100 dark:hover:text-gray-100 transition"
              data-track="immodossier valiant overview footer agb"
            >
              AGB
            </a>
            <a
              href="https://www.agentselly.ch/datenschutz/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100 dark:hover:text-gray-100 transition"
              data-track="immodossier valiant overview footer datenschutz"
            >
              Datenschutz
            </a>
            <a
              href="https://www.agentselly.ch/impressum/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100 dark:hover:text-gray-100 transition"
              data-track="immodossier valiant overview footer impressum"
            >
              Impressum
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
